/**
 * `saved 4 s ago` / `Saving…` — `docs/property-maps-v2.md` §5.3, §5.7.
 *
 * There is no Save button on a map (§12 rule 9), which only works if the
 * screen says what it has done. The pill is that promise and the one place
 * the states are named: saving, saved a moment ago, a conflict somebody has
 * to act on, and a write that failed. Board 2 prints the quiet pair *inside*
 * the active-layer chip bottom-left — hence `inline`, which drops the chip
 * and leaves the words — and gives the loud pair a chip of their own.
 *
 * **"saved" counts up.** A pill frozen at "saved" is a pill that might be
 * stale; one reading "saved 2 m ago" is a fact. It re-renders on its own
 * clock rather than on the editor's, so an idle map does not re-render.
 */
import { useEffect, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { IconSpinner, IconWarn } from '../../frame/icons.js'

export type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: number }
  | { kind: 'conflict'; who: string | null; onRefresh: () => void }
  | { kind: 'failed'; message: string }

/**
 * Whether this is a receipt or something somebody has to act on.
 *
 * Board 2 folds the quiet pair into the active-layer chip; a conflict or a
 * failure stays its own coloured chip, because it carries a link and a chip
 * inside a chip is not a thing anybody notices.
 */
export const isLoud = (state: SaveState): boolean =>
  state.kind === 'conflict' || state.kind === 'failed'

export function StatusPill({ state, inline }: { state: SaveState; inline?: boolean }) {
  const [, tick] = useState(0)

  /* One second while something is in flight, then half a minute: the words
     only change at 5 s, 1 m, 2 m, and re-rendering a pill 60 times to print
     the same sentence is work nobody asked for. */
  useEffect(() => {
    if (state.kind !== 'saved') return undefined
    const timer = window.setInterval(() => tick((n) => n + 1), 15_000)
    return () => window.clearInterval(timer)
  }, [state.kind])

  if (state.kind === 'idle') return null

  if (state.kind === 'conflict') {
    return (
      <span className={cn(
        'inline-flex items-center gap-1.5 rounded-chip bg-warn-bg px-2.5 py-1 text-sub',
        'font-semibold text-warn shadow-popover',
      )}
      >
        <IconWarn size={12} />
        {state.who ? `Updated by ${state.who}` : 'Updated somewhere else'}
        <button
          type="button"
          onClick={state.onRefresh}
          className="underline underline-offset-2"
        >
          Refresh
        </button>
      </span>
    )
  }

  if (state.kind === 'failed') {
    return (
      <span className={cn(
        'inline-flex items-center gap-1.5 rounded-chip bg-danger-bg px-2.5 py-1 text-sub',
        'font-semibold text-danger shadow-popover',
      )}
      >
        <IconWarn size={12} />
        {state.message}
      </span>
    )
  }

  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 text-sub text-text-muted',
      inline ? '' : 'rounded-chip bg-surface px-2.5 py-1 font-semibold shadow-popover',
    )}
    >
      {state.kind === 'saving'
        ? <><IconSpinner size={12} />Saving…</>
        : `saved ${sinceWords(Date.now() - state.at)}`}
    </span>
  )
}

/** `just now`, `12 s ago`, `3 m ago`. Nothing finer than a second. */
export function sinceWords(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1_000))
  if (seconds < 5) return 'just now'
  if (seconds < 60) return `${seconds} s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} m ago`
  return `${Math.round(minutes / 60)} h ago`
}
